'use client';

import { useMemo, useState } from 'react';
import {
  describeFilters,
  type ChipContext,
  type FilterChip,
} from '@/new_components/searchresults/activeFilters';
import type { FilterParams } from '@/new_components/searchresults/CompanySearchSidebar';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/use-locale';

/**
 * Shared hook so the dialog preview and the card cannot describe the same
 * search differently.
 */
export function useFilterChips(filters: FilterParams | null): FilterChip[] {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return useMemo(() => {
    if (!filters) return [];
    const ctx: ChipContext = {
      t: (key, options) => t(key, options) as string,
      lang: locale,
    };
    return describeFilters(filters, ctx);
  }, [filters, t, locale]);
}

/**
 * The filter set, compact.
 *
 * A saved search is only recognisable if its criteria are visible — a name
 * alone is ambiguous a week later. `max` keeps a card to one or two lines;
 * the overflow counter is a button rather than a tooltip so the full set is
 * reachable on touch devices too.
 */
export function FilterChips({
  filters,
  max = 4,
  className = '',
  interactive = true,
}: {
  filters: FilterParams | null;
  max?: number;
  /** Static overflow indicator when chips are inside a row link. */
  interactive?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const chips = useFilterChips(filters);
  const [expanded, setExpanded] = useState(false);

  if (chips.length === 0) {
    return (
      <span className={`text-xs text-muted-foreground ${className}`}>
        {t('savedSearches.noFilters', 'No filters — the whole register')}
      </span>
    );
  }

  const shown = expanded ? chips : chips.slice(0, max);
  const hidden = chips.length - shown.length;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {shown.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80"
        >
          {chip.label}
        </span>
      ))}
      {hidden > 0 &&
        (interactive ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center rounded-md border border-dashed px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
          >
            +{hidden}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">+{hidden}</span>
        ))}
    </div>
  );
}
